"use client";

import { InputHTMLAttributes, forwardRef } from "react";
import { Check, Minus } from "lucide-react";

type CheckboxSize = "sm" | "md" | "lg";

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  /** Size variant */
  size?: CheckboxSize;
  label?: string;
  description?: string;
  indeterminate?: boolean;
}

const sizeStyles: Record<CheckboxSize, { box: string; icon: string }> = {
  sm: { box: "w-4 h-4", icon: "w-2.5 h-2.5" },
  md: { box: "w-5 h-5", icon: "w-3 h-3" },
  lg: { box: "w-6 h-6", icon: "w-4 h-4" },
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { size = "md", label, description, indeterminate = false, className = "", id, onChange, readOnly, ...props },
  ref
) {
  const checkboxId =
    id || `checkbox-${label?.toLowerCase().replace(/\s/g, "-") || "item"}`;
  const sizeStyle = sizeStyles[size];

  // onChange가 없으면 readOnly로 처리
  const isReadOnly = readOnly || (!onChange && props.checked !== undefined);

  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <div className="relative flex items-center justify-center">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          onChange={onChange}
          readOnly={isReadOnly}
          className={`peer ${sizeStyle.box} appearance-none rounded border-2 border-[var(--color-border)] bg-[var(--color-surface)] transition-all cursor-pointer checked:bg-[var(--color-button-primary)] checked:border-[var(--color-button-primary)] hover:border-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-text-muted)] focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed`}
          {...props}
        />
        {indeterminate ? (
          <Minus className={`absolute ${sizeStyle.icon} text-[var(--color-button-primary-text)] pointer-events-none opacity-0 peer-checked:opacity-100`} />
        ) : (
          <Check className={`absolute ${sizeStyle.icon} text-[var(--color-button-primary-text)] pointer-events-none opacity-0 peer-checked:opacity-100`} />
        )}
      </div>

      {(label || description) && (
        <div className="flex-1">
          {label && (
            <label
              htmlFor={checkboxId}
              className="block text-sm font-medium text-[var(--color-text-primary)] cursor-pointer select-none"
            >
              {label}
            </label>
          )}
          {description && (
            <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{description}</p>
          )}
        </div>
      )}
    </div>
  );
});

export { Checkbox, type CheckboxProps, type CheckboxSize };
